"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LinuxVersionResolver = void 0;
/* --------------------------------------------------------------------------------------------
 *  Licensed to the .NET Foundation under one or more agreements.
*  The .NET Foundation licenses this file to you under the MIT license.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 * ------------------------------------------------------------------------------------------ */
const path = require("path");
const EventStreamEvents_1 = require("../EventStream/EventStreamEvents");
const CommandExecutor_1 = require("../Utils/CommandExecutor");
const FileUtilities_1 = require("../Utils/FileUtilities");
const InstallIdUtilities_1 = require("../Utils/InstallIdUtilities");
const DebianDistroSDKProvider_1 = require("./DebianDistroSDKProvider");
const GenericDistroSDKProvider_1 = require("./GenericDistroSDKProvider");
const RedHatDistroSDKProvider_1 = require("./RedHatDistroSDKProvider");
const StringConstants_1 = require("./StringConstants");
const VersionResolver_1 = require("./VersionResolver");
const versionUtils = require("./VersionUtilities");
/**
 * This class is responsible for detecting the distro and version of the Linux OS.
 * It also serves as the entry point to installation via a specific distro implementation
 * by implementing version validation that normally happens inside of a windows or mac .net installer.
 * Since those don't exist for linux, we need to manually implement and check certain edge-cases before allowing the installation to occur.
 */
class LinuxVersionResolver {
    constructor(workerContext, utilityContext, executor = null, distroProvider = null) {
        this.workerContext = workerContext;
        this.utilityContext = utilityContext;
        this.distro = null;
        this.distroSDKProvider = null;
        this.okUpdateExitCode = 11188; // Arbitrary number that is not shared or used by other things we rely on as an exit code
        this.okAlreadyExistsExitCode = 11166;
        this.conflictingInstallErrorMessage = `A dotnet installation was found which indicates dotnet that was installed via Microsoft package feeds. But for this distro and version, we only acquire .NET via the distro feeds.
    You should not mix distro feed and microsoft feed installations. To continue, please completely remove this version of dotnet to continue by following https://learn.microsoft.com/dotnet/core/install/remove-runtime-sdk-versions?pivots=os-linux.
    Your install location: `;
        this.conflictingCustomInstallErrorMessage = `A custom dotnet installation exists.
    If we were to install another .NET, we would break your custom .NET installation, so the installation request has been refused.
    If you would like to proceed with installing .NET automatically for VS Code, you must remove this custom installation.
    Your custom install is located at: `;
        this.baseUnsupportedDistroErrorMessage = 'We are unable to detect the distro or version of your machine';
        this.unsupportedDistroErrorMessage = `Your current distro is not yet supported. We are expanding this list based on community feed back and contributions.
If you would like to contribute to the list of supported distros, please visit: https://github.com/dotnet/vscode-dotnet-runtime/blob/main/Documentation/adding-distros.md`;
        this.redhatUnsupportedDistroErrorMessage = `Red Hat Enterprise Linux 7.0 is currently not supported.
Follow the instructions here to download the .NET SDK: https://learn.microsoft.com/en-us/dotnet/core/install/linux-rhel#rhel-7--net-6.
Or, install Red Hat Enterprise Linux 8.0 or Red Hat Enterprise Linux 9.0 from https://access.redhat.com/downloads/;`;
        // This includes all distros that we officially support for this tool as a company. If a distro is not in this list, it can still have community member support.
        this.microsoftSupportedDistroIds = [StringConstants_1.RED_HAT_DISTRO_INFO_KEY, StringConstants_1.UBUNTU_DISTRO_INFO_KEY];
        this.commandRunner = executor !== null && executor !== void 0 ? executor : new CommandExecutor_1.CommandExecutor(this.workerContext, this.utilityContext);
        this.versionResolver = new VersionResolver_1.VersionResolver(workerContext);
        if (distroProvider) {
            this.distroSDKProvider = distroProvider;
        }
        this.acquireCtx = this.workerContext.acquisitionContext;
    }
    /**
     * @remarks relies on /etc/os-release currently. public for testing purposes.
     * @returns The linux distro and version thats running this app. Should only ever be ran on linux.
     */
    getRunningDistro() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d;
            if (this.distro) {
                return this.distro;
            }
            const mainOSDeclarationFile = `/etc/os-release`;
            // Some distros may not include the os-release file specified by system d, https://0pointer.de/blog/projects/os-release and this is a recommended fallback https://man7.org/linux/man-pages/man5/os-release.5.html
            const backupOSDeclarationFile = `/usr/lib/os-release`;
            const osDeclarationFile = (yield new FileUtilities_1.FileUtilities().exists(mainOSDeclarationFile)) ? mainOSDeclarationFile : backupOSDeclarationFile;
            const distroNameKey = 'NAME';
            const distroVersionKey = 'VERSION_ID';
            try {
                const osInfo = (yield new FileUtilities_1.FileUtilities().read(osDeclarationFile)).split('\n');
                // We need to remove the quotes from the KEY="VALUE"\n pairs returned by the command stdout, and then turn it into a dictionary. We can't use replaceAll for older browsers.
                // Replace only replaces one quote, so we remove the 2nd one later.
                const infoWithQuotesRemoved = osInfo.map(x => x.replace('"', ''));
                const infoWithSeparatedKeyValues = infoWithQuotesRemoved.map(x => x.split('='));
                const keyValueMap = Object.fromEntries(infoWithSeparatedKeyValues.map(x => [x[0], x[1]]));
                // Remove the 2nd quotes.
                const distroName = (_b = (_a = keyValueMap[distroNameKey]) === null || _a === void 0 ? void 0 : _a.replace('"', '')) !== null && _b !== void 0 ? _b : '';
                const distroVersion = (_d = (_c = keyValueMap[distroVersionKey]) === null || _c === void 0 ? void 0 : _c.replace('"', '')) !== null && _d !== void 0 ? _d : '';
                if (distroName === '' || distroVersion === '') {
                    const error = new EventStreamEvents_1.DotnetAcquisitionDistroUnknownError(new EventStreamEvents_1.EventCancellationError('DotnetAcquisitionDistroUnknownError', this.baseUnsupportedDistroErrorMessage), (0, InstallIdUtilities_1.getInstallFromContext)(this.workerContext));
                    this.workerContext.eventStream.post(error);
                    throw error.error;
                }
                const pair = { distro: distroName, version: distroVersion };
                return pair;
            }
            catch (error) {
                const err = new EventStreamEvents_1.DotnetAcquisitionDistroUnknownError(new EventStreamEvents_1.EventCancellationError('DotnetAcquisitionDistroUnknownError', `${this.baseUnsupportedDistroErrorMessage} ... does /etc/os-release or /usr/lib/os-release exist?`), (0, InstallIdUtilities_1.getInstallFromContext)(this.workerContext));
                this.workerContext.eventStream.post(err);
                throw err.error;
            }
        });
    }
    /**
     * @remarks we need to call the command executor to allow us to test this class.
     * The command executor needs to be able to run async commands. We cant call async commands in the constructor.
     * So we have to use this pattern of calling initialize to verify our members are not null in each function.
     */
    Initialize() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.distro) {
                this.distro = yield this.getRunningDistro();
            }
            if (!this.distroSDKProvider) {
                this.distroSDKProvider = this.DistroProviderFactory(this.distro);
            }
            if (!this.distro || !this.distroSDKProvider) {
                const error = new EventStreamEvents_1.DotnetAcquisitionDistroUnknownError(new EventStreamEvents_1.EventCancellationError('DotnetAcquisitionDistroUnknownError', `${this.baseUnsupportedDistroErrorMessage} ... we cannot initialize.`), (0, InstallIdUtilities_1.getInstallFromContext)(this.workerContext));
                this.workerContext.eventStream.post(error);
                throw error.error;
            }
            else {
                if (!this.microsoftSupportedDistroIds.includes(this.distro.distro)) {
                    // UX: Could eventually add a 'Go away' button via the callback:
                    this.utilityContext.ui.showInformationMessage(`Automated SDK installation for the distro ${this.distro.distro} is not officially supported, except for community implemented and Microsoft approved support.
If you experience issues, please reach out on https://github.com/dotnet/vscode-dotnet-runtime/issues.`, () => { });
                }
            }
        });
    }
    isRedHatVersion7(rhelVersion) {
        if (Math.floor(parseFloat(rhelVersion)) === 7) {
            return true;
        }
        return false;
    }
    DistroProviderFactory(distroAndVersion) {
        switch (distroAndVersion.distro) {
            // Implement any custom logic for a Distro Class in a new DistroSDKProvider and add it to the factory here.
            case null:
                const unknownDistroErr = new EventStreamEvents_1.DotnetAcquisitionDistroUnknownError(new EventStreamEvents_1.EventCancellationError('DotnetAcquisitionDistroUnknownError', this.unsupportedDistroErrorMessage), (0, InstallIdUtilities_1.getInstallFromContext)(this.workerContext));
                this.workerContext.eventStream.post(unknownDistroErr);
                throw unknownDistroErr.error;
            case StringConstants_1.RED_HAT_DISTRO_INFO_KEY:
                if (this.isRedHatVersion7(distroAndVersion.version)) {
                    const unsupportedRhelErr = new EventStreamEvents_1.DotnetAcquisitionDistroUnknownError(new EventStreamEvents_1.EventCancellationError('DotnetAcquisitionDistroUnknownError', this.redhatUnsupportedDistroErrorMessage), (0, InstallIdUtilities_1.getInstallFromContext)(this.workerContext));
                    this.workerContext.eventStream.post(unsupportedRhelErr);
                    throw unsupportedRhelErr.error;
                }
                return new RedHatDistroSDKProvider_1.RedHatDistroSDKProvider(distroAndVersion, this.workerContext, this.utilityContext);
            case StringConstants_1.DEBIAN_DISTRO_INFO_KEY:
                return new DebianDistroSDKProvider_1.DebianDistroSDKProvider(distroAndVersion, this.workerContext, this.utilityContext);
            default:
                return new GenericDistroSDKProvider_1.GenericDistroSDKProvider(distroAndVersion, this.workerContext, this.utilityContext);
        }
    }
    /**
     *
     * @param supportStatus The support status of this distro and version pair.
     * @param fullySpecifiedDotnetVersion The version of dotnet requested to install, upgrade, etc.
     * @remarks Throws a specific error below if a conflicting install type of dotnet exists on linux.
     * Microsoft and distro feed packages together cause system instability with dotnet, so we don't want to let people get into those states.
     * Eventually, we could add logic to remove them for users, but that may require consent first.
     */
    VerifyNoConflictInstallTypeExists(supportStatus, fullySpecifiedDotnetVersion) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.Initialize();
            if (supportStatus === "DISTRO" /* DotnetDistroSupportStatus.Distro */) {
                const microsoftFeedDir = this.distroSDKProvider.getExpectedDotnetMicrosoftFeedInstallationDirectory();
                if (yield new FileUtilities_1.FileUtilities().exists(microsoftFeedDir)) {
                    const err = new EventStreamEvents_1.DotnetConflictingLinuxInstallTypesError(new EventStreamEvents_1.EventCancellationError('DotnetConflictingLinuxInstallTypesError', this.conflictingInstallErrorMessage + microsoftFeedDir), (0, InstallIdUtilities_1.getInstallFromContext)(this.workerContext));
                    this.workerContext.eventStream.post(err);
                    throw err.error;
                }
            }
            else if (supportStatus === "MICROSOFT" /* DotnetDistroSupportStatus.Microsoft */) {
                const distroFeedDir = this.distroSDKProvider.getExpectedDotnetDistroFeedInstallationDirectory();
                if (yield new FileUtilities_1.FileUtilities().exists(distroFeedDir)) {
                    const err = new EventStreamEvents_1.DotnetConflictingLinuxInstallTypesError(new EventStreamEvents_1.EventCancellationError('DotnetConflictingLinuxInstallTypesError', this.conflictingInstallErrorMessage + distroFeedDir), (0, InstallIdUtilities_1.getInstallFromContext)(this.workerContext));
                    this.workerContext.eventStream.post(err);
                    throw err.error;
                }
            }
        });
    }
    /**
     * Similar to VerifyNoConflictInstallTypeExists, but checks if a custom install exists. We don't want to override that.
     * It could also cause unstable behavior and break a users current setup.
     */
    VerifyNoCustomInstallExists(supportStatus, fullySpecifiedDotnetVersion, existingInstall) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.Initialize();
            if (existingInstall && path.resolve(existingInstall) !== path.resolve(supportStatus === "DISTRO" /* DotnetDistroSupportStatus.Distro */ ? this.distroSDKProvider.getExpectedDotnetDistroFeedInstallationDirectory()
                : this.distroSDKProvider.getExpectedDotnetMicrosoftFeedInstallationDirectory())) {
                const err = new EventStreamEvents_1.DotnetCustomLinuxInstallExistsError(new EventStreamEvents_1.EventCancellationError('DotnetCustomLinuxInstallExistsError', this.conflictingCustomInstallErrorMessage + existingInstall), (0, InstallIdUtilities_1.getInstallFromContext)(this.workerContext));
                this.workerContext.eventStream.post(err);
                throw err.error;
            }
        });
    }
    /**
     *
     * @param fullySpecifiedDotnetVersion The version to install of the dotnet sdk.
     * @param existingInstall a path to the existing dotnet install on the machine.
     * @returns 0 if we can proceed. Will throw if a conflicting install exists. If we can update, it will do the update and return 1.
     * A string is returned in case we want to make this return more info about the update.
     * @remarks it is expected you are holding the global modifier lock when calling this function.
     */
    UpdateOrRejectIfVersionRequestDoesNotRequireInstall(fullySpecifiedDotnetVersion, existingInstall) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.Initialize();
            this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetInstallLinuxChecks(`Checking to see if we should install, update, or cancel...`));
            if (existingInstall) {
                const existingGlobalInstallSDKVersion = yield this.distroSDKProvider.getInstalledGlobalDotnetVersionIfExists();
                if (existingGlobalInstallSDKVersion && Number(versionUtils.getMajorMinor(existingGlobalInstallSDKVersion, this.workerContext.eventStream, this.workerContext)) ===
                    Number(versionUtils.getMajorMinor(fullySpecifiedDotnetVersion, this.workerContext.eventStream, this.workerContext))) {
                    const isPatchUpgrade = Number(versionUtils.getFeatureBandPatchVersion(existingGlobalInstallSDKVersion, this.workerContext.eventStream, this.workerContext)) <
                        Number(versionUtils.getFeatureBandPatchVersion(fullySpecifiedDotnetVersion, this.workerContext.eventStream, this.workerContext));
                    if (Number(versionUtils.getMajorMinor(existingGlobalInstallSDKVersion, this.workerContext.eventStream, this.workerContext)) >
                        Number(versionUtils.getMajorMinor(fullySpecifiedDotnetVersion, this.workerContext.eventStream, this.workerContext))
                        || Number(versionUtils.getFeatureBandFromVersion(existingGlobalInstallSDKVersion, this.workerContext.eventStream, this.workerContext)) >
                            Number(versionUtils.getFeatureBandFromVersion(fullySpecifiedDotnetVersion, this.workerContext.eventStream, this.workerContext))) {
                        // We shouldn't downgrade to a lower patch
                        const err = new EventStreamEvents_1.DotnetCustomLinuxInstallExistsError(new EventStreamEvents_1.EventCancellationError('DotnetCustomLinuxInstallExistsError', `An installation of ${fullySpecifiedDotnetVersion} was requested but ${existingGlobalInstallSDKVersion} is already available.`), (0, InstallIdUtilities_1.getInstallFromContext)(this.workerContext));
                        this.workerContext.eventStream.post(err);
                        throw err.error;
                    }
                    else if ((yield this.distroSDKProvider.dotnetPackageExistsOnSystem(fullySpecifiedDotnetVersion, 'sdk')) || isPatchUpgrade) {
                        // We can update instead of doing an install
                        this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetUpgradedEvent(isPatchUpgrade ?
                            `Updating .NET: Current Version: ${existingGlobalInstallSDKVersion} to ${fullySpecifiedDotnetVersion}.`
                            :
                                `Repairing .NET Packages for ${existingGlobalInstallSDKVersion}.`));
                        return (yield this.distroSDKProvider.upgradeDotnet(existingGlobalInstallSDKVersion, 'sdk')) === '0' ? String(this.okUpdateExitCode) : '1';
                    }
                    else {
                        // An existing install exists.
                        return String(this.okAlreadyExistsExitCode);
                    }
                }
                // Additional logic to check the major.minor could be added here if we wanted to prevent installing lower major.minors if an existing install existed.
            }
            return '0';
        });
    }
    // It is expected you are holding the global modifier lock when calling this function.
    ValidateAndInstallSDK(fullySpecifiedDotnetVersion) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            yield this.Initialize();
            // Verify the version of dotnet is supported
            if (!(yield this.distroSDKProvider.isDotnetVersionSupported(fullySpecifiedDotnetVersion, 'sdk'))) {
                throw new EventStreamEvents_1.EventBasedError('UnsupportedDistro', `The distro ${(_a = this.distro) === null || _a === void 0 ? void 0 : _a.distro} ${(_b = this.distro) === null || _b === void 0 ? void 0 : _b.version} does not officially support dotnet version ${fullySpecifiedDotnetVersion}.`);
            }
            // Verify there are no conflicting installs
            // Check existing installs ...
            const supportStatus = yield this.distroSDKProvider.getDotnetVersionSupportStatus(fullySpecifiedDotnetVersion, 'sdk');
            yield this.VerifyNoConflictInstallTypeExists(supportStatus, fullySpecifiedDotnetVersion);
            const existingInstall = yield this.distroSDKProvider.getInstalledGlobalDotnetPathIfExists('sdk');
            // Check for a custom install
            yield this.VerifyNoCustomInstallExists(supportStatus, fullySpecifiedDotnetVersion, existingInstall);
            // Check if we need to install or not, if we can install (if the version conflicts with an existing one), or if we can just update the existing install.
            const updateOrRejectState = yield this.UpdateOrRejectIfVersionRequestDoesNotRequireInstall(fullySpecifiedDotnetVersion, existingInstall);
            if (updateOrRejectState === '0') {
                return (yield this.distroSDKProvider.installDotnet(fullySpecifiedDotnetVersion, 'sdk')) ? '0' : '1';
            }
            else if (updateOrRejectState === String(this.okUpdateExitCode) || updateOrRejectState === String(this.okAlreadyExistsExitCode)) {
                return '0';
            }
            return String(updateOrRejectState);
        });
    }
    // @remarks It is expected you are holding the global modifier lock when calling this function.
    UninstallSDK(fullySpecifiedDotnetVersion) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.Initialize();
            return this.distroSDKProvider.uninstallDotnet(fullySpecifiedDotnetVersion, 'sdk');
        });
    }
    /**
     * This exposes the class member that may or may not be initialized before execution of this function
     * ... so other's can use it. (It is a terrible pattern but used because the ctor cannot be async.)
     * @returns the distroSDKProvider to call distro related functions on top of.
     */
    distroCall() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.Initialize();
            return this.distroSDKProvider;
        });
    }
    getRecommendedDotnetVersion(installType) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.Initialize();
            return this.distroSDKProvider.getRecommendedDotnetVersion(installType);
        });
    }
}
exports.LinuxVersionResolver = LinuxVersionResolver;
//# sourceMappingURL=LinuxVersionResolver.js.map